<template lang="pug">
  WtEntityTable#email-templates-table(
    title="Email Templates"
    :data="templatesData"
    default-sort-field="id"
    empty-message="There are no email templates created."
    :tooltip-text="tooltipText"
  )
    template(v-slot:headerRight)
      WtButton(@click.native="newTemplate") ADD NEW TEMPLATE

    BTableColumn(v-slot="column")
      a(@click="edit(column.row)")
        BIcon(
          icon="settings"
          size="is-small"
        )
    BTableColumn(
      field="subject"
      label="Email Subject"
      sortable
      v-slot="column"
    )
      | {{ column.row.subject }}
    BTableColumn(v-slot="column")
      WtButton(
        is-text
        @click.native="deleteTemplate(column.row)"
      )
        BIcon(icon="delete")

      BModal(:active.sync="modalOpen")
        .modal-card(style='width: auto')
          header.modal-card-head
            p.modal-card-title {{ saveOrUpdateText }} Template
          section.modal-card-body
            .columns
              .column
                BField(
                  :type="{ 'is-danger': errors.has('email-subject') }"
                  :message="errors.first('email-subject')"
                )
                  WtLabel(
                    slot="label"
                    text="Email Subject"
                    :tooltip-text="subjectTooltip"
                    position="is-right"
                    label-for="email-subject"
                  )
                  UniquenessCheck#email-subject(
                    name="email-subject"
                    entity="templateSubject"
                    placeholder="Subject of email"
                    v-model="editingTemplate.subject"
                    v-validate.disable="'required'"
                  )
                BField(
                  :type="{ 'is-danger': errors.has('email-body') }"
                  :message="errors.first('email-body')"
                )
                  WtLabel(
                    slot="label"
                    text="Email Body"
                    :tooltip-text="bodyTooltip"
                    position="is-right"
                    label-for="email-body"
                  )
                  BInput#email-body(
                    type="textarea"
                    name="email-body"
                    v-model="editingTemplate.body"
                    v-validate.disable="'required'"
                  )
          footer.modal-card-foot
            WtButton(color="red" @click.native="closeModal") CANCEL
            WtButton(
              :disabled="submitDisabled"
              @click.native="saveOrUpdate"
            )
              .is-uppercase {{ saveOrUpdateText }}
</template>

<script>
import { mapActions } from "vuex";
import WtEntityTable from "@@/WtEntityTable/WtEntityTable";
import WtButton from "@@/WtButton/WtButton";
import UniquenessCheck from "@@/WtUniquenessCheckInput/WtUniquenessCheckInput";
import WtLabel from "@@/WtLabel/WtLabel";

export default {
  name: "EmailTemplatesTable",

  components: {
    WtLabel,
    WtEntityTable,
    WtButton,
    UniquenessCheck
  },

  props: {
    templatesData: {
      type: Array,
      default: null
    }
  },

  data() {
    return {
      originalTemplate: null,
      editingTemplate: {
        subject: null,
        body: null
      },
      modalOpen: false,
      tooltipText: `Program admins have access to these email templates for sending to various filtered members.`,
      subjectTooltip: `This text will show as the "subject" for this email.`,
      bodyTooltip: `This would be the text content of the email. Admins can also add custom text if they wish.`
    };
  },

  computed: {
    isEdit() {
      return !!this.editingTemplate.id;
    },

    subjectChanged() {
      return (
        this.isEdit &&
        this.editingTemplate.subject !== this.originalTemplate.subject
      );
    },

    bodyChanged() {
      return (
        this.isEdit && this.editingTemplate.body !== this.originalTemplate.body
      );
    },

    submitDisabled() {
      return this.isEdit && !(this.bodyChanged || this.subjectChanged);
    },

    saveOrUpdateText() {
      return this.isEdit ? "Update" : "Submit";
    }
  },

  methods: {
    ...mapActions(["snackBar"]),

    edit(template) {
      this.modalOpen = true;
      this.originalTemplate = this._.cloneDeep(template);
      this.editingTemplate = this._.cloneDeep(template);
    },

    newTemplate() {
      this.modalOpen = true;
    },

    saveOrUpdate() {
      this.$validator.validateAll().then(result => {
        if (result) {
          return this.isEdit ? this.update() : this.save();
        }
      });
    },

    save() {
      const payload = { ...this.editingTemplate };
      this.$emit("save-template", payload);
      this.closeModal();
    },

    update() {
      let payload = {
        templateId: this.editingTemplate.id
      };

      if (this.subjectChanged) {
        const subject = { subject: this.editingTemplate.subject };
        payload = { ...payload, ...subject };
      }

      if (this.bodyChanged) {
        const body = { body: this.editingTemplate.body };
        payload = { ...payload, ...body };
      }

      this.$emit("update-template", payload);
      this.closeModal();
    },

    closeModal() {
      this.modalOpen = false;
      this.editingTemplate = {};
      this.originalTemplate = null;
    },

    deleteTemplate(template) {
      const subject = template.subject;

      this.$buefy.dialog.confirm({
        title: `Deleting Template`,
        confirmText: "Delete Template",
        type: "is-danger",
        hasIcon: true,
        message: `Are you sure you want to delete ${subject}? This cannot be undone.`,
        onConfirm: () => {
          this.$emit("delete-template", template.id);
        }
      });
    }
  }
};
</script>

<style lang="sass" scoped></style>
